import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { HardHat, LogOut, Search, Download, Loader2, Inbox, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "sc_admin_token";

const formatError = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const inputCls =
  "w-full rounded-none border border-black/15 bg-white px-4 py-3.5 text-sm text-ink placeholder:text-black/30 transition-colors duration-300 focus:border-bronze focus:outline-none focus:ring-1 focus:ring-bronze";

const Logo = () => (
  <img src="/logo.png" alt="Sunny's Construction Ltd" className="h-10 w-auto" />
);

export const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [quotes, setQuotes] = useState(null);
  const [surveys, setSurveys] = useState(null);
  const [tab, setTab] = useState("quotes");
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [q, s] = await Promise.all([
          axios.get(`${API}/admin/quotes`, { headers }),
          axios.get(`${API}/admin/surveys`, { headers }),
        ]);
        if (!cancelled) {
          setQuotes(q.data);
          setSurveys(s.data);
        }
      } catch (e) {
        if (cancelled) return;
        if (e.response?.status === 401) {
          localStorage.removeItem(TOKEN_KEY);
          setToken(null);
          toast.error("Session expired — please sign in again.");
        } else {
          setQuotes([]);
          setSurveys([]);
          toast.error("Could not load dashboard data. Please try again.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      setToken(data.access_token);
      toast.success("Welcome back.");
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setQuotes(null);
    setSurveys(null);
    setQuery("");
  };

  const filtered = useMemo(() => {
    const list = tab === "quotes" ? quotes : surveys;
    if (!list) return [];
    const q = query.trim().toLowerCase();
    if (!q) return list;
    const fields = tab === "quotes"
      ? (r) => [r.name, r.email, r.phone, r.service_type, r.message]
      : (r) => [r.name, r.phone, r.postcode, r.date, r.slot, r.notes];
    return list.filter((r) => fields(r).filter(Boolean).join(" ").toLowerCase().includes(q));
  }, [quotes, surveys, tab, query]);

  const stats = useMemo(() => {
    const qs = quotes || [];
    const ss = surveys || [];
    const weekAgo = Date.now() - 7 * 86400000;
    const counts = {};
    qs.forEach((r) => {
      counts[r.service_type] = (counts[r.service_type] || 0) + 1;
    });
    return {
      total: qs.length,
      surveys: ss.length,
      week: qs.filter((r) => new Date(r.created_at).getTime() > weekAgo).length +
        ss.filter((r) => new Date(r.created_at).getTime() > weekAgo).length,
      top: Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] || "—",
    };
  }, [quotes, surveys]);

  const exportCsv = () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    let header, rows, name;
    if (tab === "quotes") {
      header = "Date,Name,Phone,Email,Service,Message";
      rows = filtered.map((r) =>
        [new Date(r.created_at).toLocaleString("en-GB"), r.name, r.phone, r.email, r.service_type, r.message]
          .map(esc)
          .join(",")
      );
      name = "sunnys-enquiries";
    } else {
      header = "Booked On,Name,Phone,Email,Postcode,Survey Date,Time Slot,Notes";
      rows = filtered.map((r) =>
        [new Date(r.created_at).toLocaleString("en-GB"), r.name, r.phone, r.email || "", r.postcode, r.date, r.slot, r.notes]
          .map(esc)
          .join(",")
      );
      name = "sunnys-survey-bookings";
    }
    const csv = [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${name}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} ${tab === "quotes" ? "enquiries" : "survey bookings"} to CSV.`);
  };

  if (!token) {
    return (
      <div
        data-testid="admin-login-page"
        className="flex min-h-screen items-center justify-center bg-coal px-6"
      >
        <div className="w-full max-w-md border border-black/10 bg-white p-10">
          <Logo />
          <h1 className="mt-8 font-display text-3xl font-bold uppercase tracking-wide text-ink">
            Admin Sign In
          </h1>
          <p className="mt-2 text-sm text-fog">
            Private area — quote enquiries dashboard.
          </p>
          <form data-testid="admin-login-form" onSubmit={login} className="mt-8 space-y-5">
            <div>
              <label className="mb-2 block text-[11px] font-medium uppercase tracking-[0.25em] text-black/50">
                Email
              </label>
              <input
                data-testid="admin-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputCls}
                placeholder="admin@sunnysconstruction.co.uk"
              />
            </div>
            <div>
              <label className="mb-2 block text-[11px] font-medium uppercase tracking-[0.25em] text-black/50">
                Password
              </label>
              <input
                data-testid="admin-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputCls}
                placeholder="Your password"
              />
            </div>
            {error && (
              <p
                data-testid="admin-login-error"
                className="border border-bronze/40 bg-bronze/10 px-4 py-3 text-xs text-bronze"
              >
                {error}
              </p>
            )}
            <button
              data-testid="admin-login-btn"
              type="submit"
              disabled={busy}
              className="flex w-full items-center justify-center gap-3 bg-bronze px-6 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-ink transition-colors duration-300 hover:bg-ink hover:text-bronze disabled:opacity-60"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
            </button>
          </form>
          <Link
            data-testid="admin-back-link"
            to="/"
            className="mt-6 flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-black/50 transition-colors duration-300 hover:text-bronze"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to site
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-coal">
      <header className="border-b border-black/10 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5">
          <div className="flex items-center gap-5">
            <Logo />
            <span className="hidden border-l border-black/10 pl-5 text-[11px] uppercase tracking-[0.3em] text-black/50 sm:block">
              Enquiry Dashboard
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Link
              data-testid="admin-back-to-site"
              to="/"
              className="hidden border border-black/15 px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:border-bronze hover:text-bronze sm:block"
            >
              View Site
            </Link>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="flex items-center gap-2 bg-ink px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-bronze transition-colors duration-300 hover:bg-black"
            >
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-10">
        <div className="grid gap-px border border-black/10 bg-black/10 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { label: "Total Enquiries", value: stats.total, testid: "admin-stat-total" },
            { label: "Survey Bookings", value: stats.surveys, testid: "admin-stat-surveys" },
            { label: "Last 7 Days", value: stats.week, testid: "admin-stat-week" },
            { label: "Top Service", value: stats.top, testid: "admin-stat-top" },
          ].map((s) => (
            <div key={s.testid} className="bg-white p-7">
              <div className="text-[11px] uppercase tracking-[0.25em] text-black/50">
                {s.label}
              </div>
              <div
                data-testid={s.testid}
                className="mt-2 font-display text-4xl font-bold text-ink"
              >
                {s.value}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full sm:max-w-sm">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-black/40" />
            <input
              data-testid="admin-search-input"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, email, service…"
              className={`${inputCls} pl-11`}
            />
          </div>
          <button
            data-testid="admin-export-btn"
            onClick={exportCsv}
            disabled={filtered.length === 0}
            className="flex items-center justify-center gap-3 bg-ink px-6 py-3.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-bronze transition-colors duration-300 hover:bg-black disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Download className="h-4 w-4" /> Export CSV
          </button>
        </div>

        <div className="mt-8 grid grid-cols-2 gap-px border border-black/10 bg-black/10">
          {[
            { key: "quotes", label: "Quote Enquiries", count: quotes?.length ?? 0 },
            { key: "surveys", label: "Survey Bookings", count: surveys?.length ?? 0 },
          ].map((t) => (
            <button
              key={t.key}
              data-testid={`admin-tab-${t.key}`}
              onClick={() => {
                setTab(t.key);
                setQuery("");
              }}
              className={`px-5 py-4 text-left text-[11px] font-semibold uppercase tracking-[0.25em] transition-colors duration-300 ${
                tab === t.key ? "bg-ink text-bronze" : "bg-white text-ink hover:bg-steel"
              }`}
            >
              {t.label} <span className="font-display text-base">({t.count})</span>
            </button>
          ))}
        </div>

        <div className="mt-6 border border-black/10 bg-white">
          {quotes === null || surveys === null ? (
            <div data-testid="admin-loading" className="flex items-center justify-center gap-3 py-24 text-fog">
              <Loader2 className="h-5 w-5 animate-spin text-bronze" />
              Loading dashboard…
            </div>
          ) : filtered.length === 0 ? (
            <div data-testid="admin-empty" className="flex flex-col items-center gap-4 py-24">
              <Inbox className="h-10 w-10 text-black/25" strokeWidth={1.5} />
              <p className="text-sm text-fog">
                {query
                  ? "Nothing matches your search."
                  : tab === "quotes"
                    ? "No enquiries yet — new quote requests will appear here."
                    : "No survey bookings yet — new bookings will appear here."}
              </p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between border-b border-black/10 px-5 py-3">
                <span data-testid="admin-count" className="text-[11px] uppercase tracking-[0.25em] text-black/50">
                  Showing {filtered.length} of {(tab === "quotes" ? quotes : surveys).length}{" "}
                  {tab === "quotes" ? "enquiries" : "survey bookings"}
                </span>
              </div>
              <div className="overflow-x-auto">
                {tab === "quotes" ? (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-black/10 text-left text-[11px] uppercase tracking-[0.2em] text-black/50">
                        <th className="px-5 py-4 font-medium">Date</th>
                        <th className="px-5 py-4 font-medium">Name</th>
                        <th className="px-5 py-4 font-medium">Contact</th>
                        <th className="px-5 py-4 font-medium">Service</th>
                        <th className="px-5 py-4 font-medium">Message</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((r) => (
                        <tr
                          key={r.id}
                          data-testid="admin-quote-row"
                          className="border-t border-black/5 align-top transition-colors duration-200 hover:bg-coal/60"
                        >
                          <td className="whitespace-nowrap px-5 py-4 text-black/60">
                            {new Date(r.created_at).toLocaleString("en-GB", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </td>
                          <td className="px-5 py-4 font-medium text-ink">{r.name}</td>
                          <td className="px-5 py-4 text-black/70">
                            <div>{r.phone}</div>
                            <div className="text-black/50">{r.email}</div>
                          </td>
                          <td className="px-5 py-4">
                            <span className="inline-block border border-bronze/40 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-bronze">
                              {r.service_type}
                            </span>
                          </td>
                          <td className="max-w-[300px] px-5 py-4 text-black/70">
                            <span className="line-clamp-2" title={r.message}>
                              {r.message}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-black/10 text-left text-[11px] uppercase tracking-[0.2em] text-black/50">
                        <th className="px-5 py-4 font-medium">Survey Date</th>
                        <th className="px-5 py-4 font-medium">Name</th>
                        <th className="px-5 py-4 font-medium">Phone</th>
                        <th className="px-5 py-4 font-medium">Postcode</th>
                        <th className="px-5 py-4 font-medium">Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((r) => (
                        <tr
                          key={r.id}
                          data-testid="admin-survey-row"
                          className="border-t border-black/5 align-top transition-colors duration-200 hover:bg-coal/60"
                        >
                          <td className="whitespace-nowrap px-5 py-4">
                            <div className="font-medium text-ink">
                              {new Date(`${r.date}T00:00:00`).toLocaleDateString("en-GB", {
                                weekday: "short",
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              })}
                            </div>
                            <div className="mt-0.5 text-[11px] uppercase tracking-[0.15em] text-bronze">
                              {r.slot}
                            </div>
                          </td>
                          <td className="px-5 py-4 font-medium text-ink">{r.name}</td>
                          <td className="px-5 py-4 text-black/70">
                            <div>{r.phone}</div>
                            {r.email && <div className="text-black/50">{r.email}</div>}
                          </td>
                          <td className="px-5 py-4">
                            <span className="inline-block border border-bronze/40 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-bronze">
                              {r.postcode}
                            </span>
                          </td>
                          <td className="max-w-[260px] px-5 py-4 text-black/70">
                            <span className="line-clamp-2" title={r.notes}>
                              {r.notes || "—"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};
